import { Fragment } from 'react';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { type IAdvancedSelectColumns } from '../IAdvancedSelectColumns.ts';
import { AdvancedSelectRichTextValue } from './AdvancedSelectRichTextValue.tsx';
import { getAdvancedSelectedOptionLabelValues } from './renderAdvancedSelectedOptionLabel.ts';

// Pattern per riconoscere un RichText "a blocchi": liste, allineamenti o più
// paragrafi. Servono a decidere se renderizzare in linea (col separatore " - ")
// oppure a blocco per non rompere la formattazione.
const LIST_TAG_REGEX = /<(ul|ol|li)\b/i;
const TEXT_ALIGN_REGEX = /text-align\s*:/i;
const PARAGRAPH_TAG_REGEX = /<p\b/gi;

/**
 * Indica se un valore RichText ha contenuto "a blocchi" (liste, allineamenti o
 * più paragrafi) che richiede un rendering a blocco. I RichText semplici (un
 * solo paragrafo senza liste/allineamenti) restano renderizzati in linea.
 */
function isBlockRichText(html: unknown): boolean {
  if (typeof html !== 'string') return false;
  if (LIST_TAG_REGEX.test(html)) return true;
  if (TEXT_ALIGN_REGEX.test(html)) return true;
  return (html.match(PARAGRAPH_TAG_REGEX)?.length ?? 0) > 1;
}

export interface IAdvancedSelectedOptionLabelProps {
  selectedValue: IWidgetAdvancedOption['values'] | null | undefined;
  labelFieldColumns?: string[];
  columns?: IAdvancedSelectColumns[];
}

/**
 * Etichetta dell'opzione selezionata: concatena le colonne con " - " ma rende
 * le colonne RichText come HTML sanificato (`AdvancedSelectRichTextValue`)
 * invece che come testo. Usata per la visualizzazione del valore selezionato nel
 * campo, dove la formattazione (grassetto, link, liste) deve essere preservata.
 */
export function AdvancedSelectedOptionLabel({
  selectedValue,
  labelFieldColumns,
  columns,
}: IAdvancedSelectedOptionLabelProps) {
  const values = getAdvancedSelectedOptionLabelValues(
    selectedValue,
    labelFieldColumns,
    columns,
  ).map(value => ({
    ...value,
    isBlock:
      value.type === Datatype.RichText && isBlockRichText(value.rawValue),
  }));

  return values.map((value, index) => (
    <Fragment key={value.id}>
      {/* Niente " - " attorno ai valori a blocco: andando a capo resterebbe orfano */}
      {index > 0 && !value.isBlock && !values[index - 1].isBlock ? ' - ' : null}
      {value.type === Datatype.RichText ? (
        <AdvancedSelectRichTextValue
          emptyValue=""
          inline={!value.isBlock}
          value={value.rawValue}
        />
      ) : (
        value.label
      )}
    </Fragment>
  ));
}
